import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, Chip, LinearProgress,
  Alert, Stack,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Refresh as ReplanIcon,
  NightsStay as CloseIcon,
  WbSunny as TomorrowIcon,
  Warning as WarningIcon,
  TrendingUp as StrategyIcon,
  Schedule as ScheduleIcon,
  RocketLaunch as SetupIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { rhythmApi, workItemsApi, calendarSourcesApi } from '../api/services';
import type { StartMyDayResponse, ScheduledWorkBlock, CalendarEvent, WorkItem, AnnualStrategicDriver, CapacitySummary } from '../types/models';
import PriorityChip from '../components/PriorityChip';
import StatusChip from '../components/StatusChip';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [dayData, setDayData] = useState<StartMyDayResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSetupPrompt, setShowSetupPrompt] = useState(false);
  const today = format(new Date(), 'yyyy-MM-dd');

  // Check if user is new (no work items, no calendar sources)
  useEffect(() => {
    let cancelled = false;
    const checkNewUser = async () => {
      try {
        const [items, sources] = await Promise.all([
          workItemsApi.getAll(),
          calendarSourcesApi.getAll(),
        ]);
        if (!cancelled && items.length === 0 && sources.length === 0) {
          setShowSetupPrompt(true);
        }
      } catch {
        // Ignore errors — just don't show the prompt
      }
    };
    checkNewUser();
    return () => { cancelled = true; };
  }, []);

  const loadDay = useCallback(async (action: 'start' | 'replan' = 'start') => {
    setLoading(true);
    setError(null);
    try {
      const data = action === 'start'
        ? await rhythmApi.startMyDay()
        : await rhythmApi.replan();
      setDayData(data);
      setShowSetupPrompt(false);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load day');
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">
            {format(new Date(), 'EEEE, d MMMM yyyy')}
          </Typography>
          {dayData?.plan.dailyTheme && (
            <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
              Theme: {dayData.plan.dailyTheme}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<StartIcon />} onClick={() => loadDay('start')} disabled={loading}>
            Start My Day
          </Button>
          <Button variant="outlined" startIcon={<ReplanIcon />} onClick={() => loadDay('replan')} disabled={loading || !dayData}>
            Replan
          </Button>
          <Button variant="outlined" startIcon={<TomorrowIcon />} onClick={async () => {
            setLoading(true);
            try {
              const data = await rhythmApi.planTomorrow();
              setDayData(data);
            } catch (err: any) {
              setError(err.message);
            } finally {
              setLoading(false);
            }
          }} disabled={loading}>
            Plan Tomorrow
          </Button>
        </Stack>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Setup prompt for new users */}
      {showSetupPrompt && !dayData && !loading && (
        <Card sx={{ mb: 3, borderLeft: 3, borderColor: 'primary.main' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2.5, py: 2.5 }}>
            <SetupIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>Welcome to Cadence!</Typography>
              <Typography color="text.secondary">
                Complete your setup to get started. Connect your calendar, set your working hours, and optionally invite your EA.
              </Typography>
            </Box>
            <Button variant="contained" onClick={() => navigate('/getting-started')} sx={{ whiteSpace: 'nowrap' }}>
              Complete Setup
            </Button>
          </CardContent>
        </Card>
      )}

      {!dayData && !loading && (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>Ready to begin</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Click "Start My Day" to generate your AI-assisted plan, sync your calendar, and build your schedule.
          </Typography>
          <Button variant="contained" size="large" startIcon={<StartIcon />} onClick={() => loadDay('start')}>
            Start My Day
          </Button>
        </Card>
      )}

      {dayData && (
        <Grid container spacing={2.5}>
          {/* Capacity Summary */}
          <Grid size={{ xs: 12, md: 4 }}>
            <CapacityCard capacity={dayData.capacity} />
          </Grid>

          {/* Plan Status */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Plan Status</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <StatusChip status={dayData.plan.status} />
                  {dayData.plan.isOverloaded && (
                    <Chip icon={<WarningIcon />} label="Overloaded" size="small" color="error" variant="outlined" />
                  )}
                </Box>
                {dayData.warnings.length > 0 && (
                  <Box>
                    {dayData.warnings.map((w, i) => (
                      <Alert key={i} severity="warning" sx={{ mb: 1, py: 0 }}>{w}</Alert>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Strategic Drivers */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <StrategyIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  Annual Strategy
                </Typography>
                {dayData.strategicDrivers.map((driver) => (
                  <Box key={driver.id} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>{driver.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{driver.progressPercentage}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={driver.progressPercentage}
                      sx={{ height: 4, borderRadius: 2 }}
                    />
                  </Box>
                ))}
                {dayData.strategicDrivers.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No strategic drivers configured</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* AI Briefing */}
          {dayData.aiBriefing && (
            <Grid size={{ xs: 12 }}>
              <Card sx={{ borderLeft: 3, borderColor: 'primary.main' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="primary.main" gutterBottom>AI Briefing</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{dayData.aiBriefing}</Typography>
                  {dayData.aiRisks && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="warning.main" gutterBottom>Risks</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{dayData.aiRisks}</Typography>
                    </Box>
                  )}
                  {dayData.aiStrategyAlignment && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="success.main" gutterBottom>Strategy Alignment</Typography>
                      <Typography variant="body2">{dayData.aiStrategyAlignment}</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Scheduled Timeline */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <ScheduleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                  Scheduled Timeline
                </Typography>
                <TimelineView blocks={dayData.scheduledBlocks} events={dayData.calendarEvents} />
              </CardContent>
            </Card>
          </Grid>

          {/* Overflow & Carry Forward */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <Stack spacing={2.5}>
              {dayData.overflowItems.length > 0 && (
                <Card sx={{ borderLeft: 3, borderColor: 'warning.main' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="warning.main" gutterBottom>
                      Overflow Items ({dayData.overflowItems.length})
                    </Typography>
                    {dayData.overflowItems.map((item) => (
                      <WorkItemRow key={item.id} item={item} />
                    ))}
                  </CardContent>
                </Card>
              )}
              {dayData.carryForwardItems.length > 0 && (
                <Card sx={{ borderLeft: 3, borderColor: 'error.main' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="error.main" gutterBottom>
                      Carry Forward ({dayData.carryForwardItems.length})
                    </Typography>
                    {dayData.carryForwardItems.map((item) => (
                      <WorkItemRow key={item.id} item={item} />
                    ))}
                  </CardContent>
                </Card>
              )}
              {dayData.carriedForwardFromYesterday.length > 0 && (
                <Card sx={{ borderLeft: 3, borderColor: 'secondary.main' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="secondary.main" gutterBottom>
                      Carried From Yesterday ({dayData.carriedForwardFromYesterday.length})
                    </Typography>
                    {dayData.carriedForwardFromYesterday.map((item) => (
                      <WorkItemRow key={item.id} item={item} />
                    ))}
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

function CapacityCard({ capacity }: { capacity: CapacitySummary }) {
  const totalMinutes = capacity.availableMinutes + capacity.meetingMinutes;
  const meetingPct = totalMinutes > 0 ? (capacity.meetingMinutes / totalMinutes) * 100 : 0;
  const plannedPct = totalMinutes > 0 ? (capacity.plannedMinutes / totalMinutes) * 100 : 0;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Capacity</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          <StatBox label="Available" value={`${Math.round(capacity.availableMinutes / 60 * 10) / 10}h`} />
          <StatBox label="Meetings" value={`${Math.round(capacity.meetingMinutes / 60 * 10) / 10}h`} />
          <StatBox label="Planned" value={`${Math.round(capacity.plannedMinutes / 60 * 10) / 10}h`} />
          <StatBox label="Overflow" value={`${Math.round(capacity.overflowMinutes)}m`} color={capacity.isOverloaded ? 'error.main' : undefined} />
        </Box>
        <Typography variant="caption" color="text.secondary" gutterBottom>Utilisation</Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min(capacity.utilisationPercent, 100)}
          color={capacity.utilisationPercent > 85 ? 'error' : capacity.utilisationPercent > 70 ? 'warning' : 'primary'}
          sx={{ height: 8, borderRadius: 4 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {Math.round(capacity.utilisationPercent)}%
        </Typography>
      </CardContent>
    </Card>
  );
}

function StatBox({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" sx={{ color: color || 'text.primary' }}>{value}</Typography>
    </Box>
  );
}

function TimelineView({ blocks, events }: { blocks: ScheduledWorkBlock[]; events: CalendarEvent[] }) {
  const allItems = [
    ...events.filter(e => !e.isAllDay).map(e => ({
      type: 'event' as const,
      start: e.startTime,
      end: e.endTime,
      title: e.title,
      isPrivate: e.isPrivate,
      isPriority: e.isPriorityMeeting,
    })),
    ...blocks.map(b => ({
      type: 'block' as const,
      start: b.startTime,
      end: b.endTime,
      title: b.workItem?.title || 'Work block',
      priority: b.workItem?.priority,
    })),
  ].sort((a, b) => a.start.localeCompare(b.start));

  if (allItems.length === 0) {
    return <Typography color="text.secondary" variant="body2">No scheduled items</Typography>;
  }

  return (
    <Stack spacing={0.5}>
      {allItems.map((item, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1,
            borderRadius: 1,
            bgcolor: item.type === 'event' ? 'rgba(255, 138, 101, 0.08)' : 'rgba(108, 155, 255, 0.08)',
            borderLeft: 3,
            borderColor: item.type === 'event'
              ? (item.isPriority ? 'error.main' : 'secondary.main')
              : 'primary.main',
          }}
        >
          <Typography variant="caption" sx={{ fontFamily: 'monospace', minWidth: 90, color: 'text.secondary' }}>
            {formatTime(item.start)} – {formatTime(item.end)}
          </Typography>
          <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
            {item.title}
          </Typography>
          {item.type === 'event' && item.isPrivate && (
            <Chip label="Priority" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
          )}
          {item.type === 'block' && (item as any).priority && (
            <PriorityChip priority={(item as any).priority} />
          )}
        </Box>
      ))}
    </Stack>
  );
}

function WorkItemRow({ item }: { item: WorkItem }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
      <PriorityChip priority={item.priority} />
      <Typography variant="body2" sx={{ flex: 1 }}>{item.title}</Typography>
      <Typography variant="caption" color="text.secondary">{item.estimatedMinutes}m</Typography>
    </Box>
  );
}

function formatTime(iso: string): string {
  try {
    // Handle both ISO datetime and time-only strings
    if (iso.includes('T')) {
      return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }
    return iso.substring(0, 5);
  } catch {
    return iso;
  }
}
